import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import moment from "moment";
import Swal from "sweetalert2";
import { deleteDoc, onSnapshot, updateDoc } from "firebase/firestore";
import Nav from "./Nav";
import Sidebar from "./Sidebar";
import Breadcrumb from "./Breadcrumb";
import Spinner from "./Spinner";
import CotizacionForm from "./CotizacionForm";
import { useNavContext } from "../context/NavContext";
import { useAuth } from "../context/AuthContext";
import { orgCollection, orgDocIn } from "../utils/firePaths";
import { useFunctionsPrevia } from "../hooks/useFunctionsPrevia";
import TableToolbar from "./TableToolbar";
import ModalPreviaCotizacion from "./ModalPreviaCotizacion";
import PdfPreviewCotizacionModal from "./PdfPreviewCotizacionModal";
import NotificationsPortal, { notify } from "../components/NotificationsPortal";
//import ModalEnviarCotizacion from "./ModalEnviarCotizacion";
import DataTable from "./DataTable";
import TablePagination from "./TablePagination";

moment.locale("es");

const TablaCotizaciones = ({
  cotizaciones,
  clientes,
  setCotizaciones,
  guardarCotizacion,
  modoEdicion,
  numeroCotizacion,
}) => {
  const { orgId } = useAuth();

  useFunctionsPrevia();

  const { searchTerm, setSearchTerm, proximasAVencer, proximosEventos } =
    useNavContext();

  const [loadingCotizaciones, setLoadingCotizaciones] = useState(true);
  const [estadosCeldas, setEstadosCeldas] = useState({});
  const [selectedCotizaciones, setSelectedCotizaciones] = useState([]);

  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [filterStatus, setFilterStatus] = useState("Todos");

  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [cotizacionParaEditar, setCotizacionParaEditar] = useState(null);
  const [cloneMode, setCloneMode] = useState(false);

  const [openPrevia, setOpenPrevia] = useState(false);
  const [cotizacionActiva, setCotizacionActiva] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem("columnPrefs_cotizaciones");
    return saved
      ? JSON.parse(saved)
      : {
          Folio: true,
          Cliente: true,
          Fecha: true,
          Total: true,
          Estado: true,
          Asunto: true,
        };
  });

  useEffect(() => {
    localStorage.setItem(
      "columnPrefs_cotizaciones",
      JSON.stringify(visibleColumns),
    );
  }, [visibleColumns]);

  const [sortConfig, setSortConfig] = useState({
    key: "fechaCotizacion",
    direction: "desc",
  });

  const [openPdfPreview, setOpenPdfPreview] = useState(false);
  const [cotizacionPdf, setCotizacionPdf] = useState(null);

  //const [openEnviarEmail, setOpenEnviarEmail] = useState(false);
  //const [cotizacionParaEnviar, setCotizacionParaEnviar] = useState(null);

  const ESTADO_MAP = useMemo(
    () => ({
      Pendiente: "status-pendiente",
      Aprobado: "status-aprobado",
      Rechazado: "status-rechazado",
      Vencido: "status-vencido",
    }),
    [],
  );

  const fileInputRef = useRef(null);

  const handleSort = useCallback((key) => {
    setSortConfig((prev) => {
      const direction =
        prev.key === key && prev.direction === "asc" ? "desc" : "asc";
      return { key, direction };
    });
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    if (!orgId) return;

    setLoadingCotizaciones(true);

    const cotizacionesRef = orgCollection(orgId, "cotizaciones");
    const unsubscribe = onSnapshot(
      cotizacionesRef,
      (snapshot) => {
        const updated = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

        const states = {};
        updated.forEach((cot) => {
          states[cot.id] = cot.estado || "Pendiente";
        });

        setEstadosCeldas(states);
        setCotizaciones(updated);
        setLoadingCotizaciones(false);
      },
      (error) => {
        console.error("onSnapshot cotizaciones error:", error);
        setLoadingCotizaciones(false);
      },
    );

    return () => unsubscribe();
  }, [orgId, setCotizaciones]);

  const filteredData = useMemo(() => {
    if (!cotizaciones) return [];

    const searchLower = (searchTerm || "")
      .toLowerCase()
      .replace(/\bde\b/g, "")
      .trim();

    return cotizaciones.filter((c) => {
      const estadoActual = estadosCeldas[c.id] || c.estado || "Pendiente";
      const matchStatus =
        filterStatus === "Todos" || estadoActual === filterStatus;
      if (!matchStatus) return false;

      if (!searchLower) return true;

      const fechaCot = c.fechaCotizacion
        ? moment(c.fechaCotizacion)
            .format("D MMM YYYY")
            .toLowerCase()
            .replace(/\./g, "")
        : "";

      const searchFields = [
        c.numeroCotizacion?.toString().padStart(4, "0"),
        c.nombreCliente,
        fechaCot,
        c.total?.toString(),
        estadoActual,
        c.asunto,
        c.referencia,
        c.divisa,
      ];

      const searchWords = searchLower.split(/\s+/);

      return searchWords.every((word) =>
        searchFields.some((field) =>
          field?.toString().toLowerCase().includes(word),
        ),
      );
    });
  }, [cotizaciones, searchTerm, filterStatus, estadosCeldas]);

  const sortedData = useMemo(() => {
    const { key, direction } = sortConfig || {};
    const dir = direction === "desc" ? -1 : 1;

    const getVal = (row) => {
      if (key === "estado") return estadosCeldas[row.id] || row.estado || "";
      return row?.[key];
    };

    return [...filteredData].sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);

      if (key === "fechaCotizacion") {
        const da = va ? new Date(va).getTime() : 0;
        const db = vb ? new Date(vb).getTime() : 0;
        return (da - db) * dir;
      }

      if (key === "total" || key === "numeroCotizacion") {
        const na =
          key === "numeroCotizacion"
            ? Number(String(va || "").replace(/\D/g, "")) || 0
            : Number(va || 0) || 0;

        const nb =
          key === "numeroCotizacion"
            ? Number(String(vb || "").replace(/\D/g, "")) || 0
            : Number(vb || 0) || 0;

        return (na - nb) * dir;
      }

      const sa = (va ?? "").toString().toLowerCase();
      const sb = (vb ?? "").toString().toLowerCase();
      return sa.localeCompare(sb) * dir;
    });
  }, [filteredData, sortConfig, estadosCeldas]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / itemsPerPage));

  const currentItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return sortedData.slice(start, end);
  }, [sortedData, currentPage, itemsPerPage]);

  // ✅ Select All (MISMA lógica que tú: solo página actual)
  const toggleSelectAll = useCallback(() => {
    const pageIds = currentItems.map((i) => i.id);
    const allSelected =
      pageIds.length > 0 &&
      pageIds.every((id) => selectedCotizaciones.includes(id));

    if (allSelected) {
      setSelectedCotizaciones((prev) =>
        prev.filter((id) => !pageIds.includes(id)),
      );
    } else {
      setSelectedCotizaciones((prev) =>
        Array.from(new Set([...prev, ...pageIds])),
      );
    }
  }, [currentItems, selectedCotizaciones]);

  const toggleEstado = useCallback(
    async (id) => {
      const ciclos = ["Pendiente", "Aprobado", "Rechazado", "Vencido"];
      const actual = estadosCeldas[id] || "Pendiente";
      const nuevo = ciclos[(ciclos.indexOf(actual) + 1) % ciclos.length];

      setEstadosCeldas((prev) => ({ ...prev, [id]: nuevo }));

      try {
        const docRef = orgDocIn(orgId, "cotizaciones", id);
        await updateDoc(docRef, { estado: nuevo });
      } catch (e) {
        console.error(e);
      }
    },
    [estadosCeldas, orgId],
  );

  const handleDeleteSelected = async () => {
    if (!orgId) {
      Swal.fire({
        title: "Error",
        text: "No se detectó orgId. Intenta recargar la página o seleccionar tu organización.",
        icon: "error",
        customClass: { popup: "swal-popup-reset" },
      });
      return;
    }

    if (selectedCotizaciones.length === 0) return;

    const count = selectedCotizaciones.length;
    const esSingular = count === 1;
    const textoCotizacion = esSingular ? "cotización" : "cotizaciones";
    const tituloModal = esSingular
      ? "Eliminar Cotización"
      : "Eliminar Cotizaciones";

    const result = await Swal.fire({
      html: `
      <div class="custom-modal-container">
        <div class="custom-modal-header">
          <div class="custom-modal-icon-box">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" x2="12" y1="8" y2="12"></line>
              <line x1="12" x2="12.01" y1="16" y2="16"></line>
            </svg>
          </div>
          <h3 class="custom-modal-title">${tituloModal}</h3>
        </div>
        <p class="custom-modal-description">¿Estás seguro de que deseas eliminar ${count} ${textoCotizacion}?</p>
        <div class="custom-modal-actions">
          <button id="cancel-btn" class="btn-modal-cancel">Cancelar</button>
          <button id="confirm-btn" class="btn-modal-delete">Eliminar</button>
        </div>
      </div>
    `,
      showConfirmButton: false,
      showCancelButton: false,
      buttonsStyling: false,
      allowOutsideClick: false,
      customClass: {
        popup: "swal-popup-reset",
        htmlContainer: "swal-html-reset",
      },
      didOpen: () => {
        document
          .getElementById("cancel-btn")
          .addEventListener("click", () => Swal.close());

        document
          .getElementById("confirm-btn")
          .addEventListener("click", () => Swal.clickConfirm());
      },
    });

    if (!result.isConfirmed) return;

    try {
      await Promise.all(
        selectedCotizaciones.map(async (id) => {
          const ref = orgDocIn(orgId, "cotizaciones", id);
          await deleteDoc(ref);
        }),
      );

      setSelectedCotizaciones([]);

      notify({
        type: "success",
        message:
          count === 1
            ? "1 cotización eliminada exitosamente"
            : `${count} cotización(es) eliminada(s) exitosamente`,
        duration: 3500,
      });
    } catch (error) {
      console.error(`Error al eliminar ${textoCotizacion}:`, error);

      notify({
        type: "error",
        message: `No se pudo eliminar: ${error.message}`,
        duration: 4500,
      });
    }
  };

  const handleEditSelected = () => {
    if (selectedCotizaciones.length !== 1) return;

    const selectedId = selectedCotizaciones[0];
    const selected = (cotizaciones || []).find((c) => c.id === selectedId);
    if (!selected) return;

    const selectedWithEstado = {
      ...selected,
      estado: estadosCeldas[selectedId] || selected.estado || "Pendiente",
    };

    setOpenPrevia(false);
    setCotizacionActiva(null);
    setCotizacionParaEditar(selectedWithEstado);
    setCloneMode(false);
    setMostrarFormulario(true);
  };

  const buildMailtoForCotizacion = (c) => {
    const folio =
      c.numeroCotizacionFormatted || c.numeroCotizacion || c.folio || "—";
    const cliente = c.nombreCliente || c.cliente || "Cliente";
    const total = Number(c.total || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    const to = (c.clienteEmail || c.correo || c.email || "").trim();
    const subject = `Cotización ${folio} - ${cliente}`;

    const body = `Hola,

Adjunto enviamos la cotización ${folio} por un total de $${total}.

Quedamos a su disposición para cualquier duda.

Saludos cordiales.`;

    const params = new URLSearchParams({ subject, body });
    return `mailto:${encodeURIComponent(to)}?${params.toString()}`;
  };

  // ✅ Cerrar dropdowns al click afuera (tu lógica)
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        showColumnConfig &&
        !e.target.closest(".column-config") &&
        !e.target.closest('[data-dd="columns"]')
      ) {
        setShowColumnConfig(false);
      }
      if (
        showExportDropdown &&
        !e.target.closest(".export-menu") &&
        !e.target.closest('[data-dd="export"]')
      ) {
        setShowExportDropdown(false);
      }
      if (
        showFilterDropdown &&
        !e.target.closest(".filter-panel") &&
        !e.target.closest('[data-dd="filter"]')
      ) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showColumnConfig, showExportDropdown, showFilterDropdown]);

  const columnsCotizaciones = useMemo(() => {
    return [
      {
        id: "folio",
        label: "Folio",
        sortKey: "numeroCotizacion",
        visible: visibleColumns.Folio,
        cellClassName: "table-td col-center",
        render: (cot) =>
          cot.numeroCotizacion?.toString().padStart(4, "0") || "---",
      },
      {
        id: "fecha",
        label: "Fecha",
        sortKey: "fechaCotizacion",
        visible: visibleColumns.Fecha,
        cellClassName: "table-td col-center",
        render: (cot) =>
          cot.fechaCotizacion
            ? moment(cot.fechaCotizacion).format("D MMM YYYY").replace(".", "")
            : "---",
      },
      {
        id: "cliente",
        label: "Cliente",
        sortKey: "nombreCliente",
        visible: visibleColumns.Cliente,
        cellClassName: "table-td col-left",
        render: (cot) => (
          <span style={{ fontWeight: 300, color: "#111827" }}>
            {cot.nombreCliente || "---"}
          </span>
        ),
      },
      {
        id: "total",
        label: "Total",
        sortKey: "total",
        visible: visibleColumns.Total,
        cellClassName: "table-td col-center",
        render: (cot) => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              lineHeight: "1.2",
            }}
          >
            <span style={{ fontSize: 14, color: "#111827", fontWeight: 400 }}>
              $
              {Number(cot.total || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
            <span
              style={{
                fontSize: 11,
                color: "#9ca3af",
                textTransform: "uppercase",
              }}
            >
              {cot.divisa || "MXN"}
            </span>
          </div>
        ),
      },
      {
        id: "estado",
        label: "Estado",
        sortKey: "estado",
        visible: visibleColumns.Estado,
        cellClassName: "table-td col-center",
        render: (cot) => {
          const estado = estadosCeldas[cot.id] || cot.estado || "Pendiente";
          return (
            <span
              onClick={(e) => {
                e.stopPropagation();
                toggleEstado(cot.id);
              }}
              className={`badge-status ${ESTADO_MAP[estado] || "status-pendiente"}`}
              title="Click para cambiar estado"
            >
              {estado}
            </span>
          );
        },
      },
      {
        id: "asunto",
        label: "Asunto",
        sortKey: "asunto",
        visible: visibleColumns.Asunto,
        cellClassName: "table-td col-left",
        render: (cot) => (
          <span style={{ color: "#64748b" }}>
            {cot.asunto || cot.referencia || "---"}
          </span>
        ),
      },
    ];
  }, [visibleColumns, estadosCeldas, toggleEstado, ESTADO_MAP]);

  return (
    <>
      <NotificationsPortal />

      <div className="no-sticky">
        <Nav
          proximasAVencer={proximasAVencer}
          proximosEventos={proximosEventos}
        />
        <Sidebar />

        <div className="main-content-wrapper">
          <div style={{ marginBottom: "25px" }}>
            <Breadcrumb />
            <h1 className="page-title">Cotizaciones de Clientes</h1>
          </div>

          <TableToolbar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            searchPlaceholder="Buscar por folio o cliente..."
            showFilterDropdown={showFilterDropdown}
            setShowFilterDropdown={setShowFilterDropdown}
            showColumnConfig={showColumnConfig}
            setShowColumnConfig={setShowColumnConfig}
            showExportDropdown={showExportDropdown}
            setShowExportDropdown={setShowExportDropdown}
            visibleColumns={visibleColumns}
            setVisibleColumns={setVisibleColumns}
            filterStatus={filterStatus}
            setFilterStatus={(v) => {
              setFilterStatus(v);
              setCurrentPage(1);
            }}
            filterOptions={[
              "Todos",
              "Pendiente",
              "Aprobado",
              "Rechazado",
              "Vencido",
            ]}
            onAdd={() => {
              setCotizacionParaEditar(null);
              setCloneMode(false);
              setMostrarFormulario(true);
            }}
            addButtonTooltip="Nueva Cotización"
            fileInputRef={fileInputRef}
            onExport={() => console.log("Exportando...")}
          />

          {selectedCotizaciones.length > 0 && (
            <div className="selection-bar">
              <span>
                {selectedCotizaciones.length === 1
                  ? "1 seleccionado"
                  : `${selectedCotizaciones.length} seleccionado(s)`}
              </span>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  className="btn-selection btn-edit"
                  style={{ opacity: selectedCotizaciones.length > 1 ? 0.5 : 1 }}
                  disabled={selectedCotizaciones.length > 1}
                  onClick={handleEditSelected}
                >
                  <i className="bi bi-pencil-square" /> <span>Editar</span>
                </button>
                <button
                  className="btn-selection btn-delete"
                  onClick={handleDeleteSelected}
                >
                  <i className="bi bi-trash" /> <span>Eliminar</span>
                </button>
              </div>
            </div>
          )}

          <div className="card-table">
            <DataTable
              loading={loadingCotizaciones}
              loadingFallback={
                <div style={{ padding: "50px" }}>
                  <Spinner />
                </div>
              }
              rows={currentItems}
              columns={columnsCotizaciones}
              selectedIds={selectedCotizaciones}
              onToggleAll={toggleSelectAll}
              onToggleRow={(id) =>
                setSelectedCotizaciones((prev) =>
                  prev.includes(id)
                    ? prev.filter((x) => x !== id)
                    : [...prev, id],
                )
              }
              sortConfig={sortConfig}
              onSort={handleSort}
              onRowClick={(row) => {
                setCotizacionActiva({
                  ...row,
                  estado: estadosCeldas[row.id] || row.estado || "Pendiente",
                });
                setOpenPrevia(true);
              }}
              emptyTitle="Aún no hay cotizaciones."
              emptySubtitle="Crea una cotización nueva o importa un archivo para comenzar."
              emptyIcon={
                <img
                  src="/img/no-data.svg"
                  alt="Sin datos"
                  style={{ width: 220, maxWidth: "70%", opacity: 0.95 }}
                />
              }
              footer={
                <TablePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={(p) => setCurrentPage(p)}
                  itemsPerPage={itemsPerPage}
                  onItemsPerPageChange={(n) => {
                    setItemsPerPage(n);
                    setCurrentPage(1);
                  }}
                  itemsPerPageOptions={[10, 25, 50]}
                  selectedCount={selectedCotizaciones.length}
                  selectedLabelSingular="cotización"
                  selectedLabelPlural="cotizaciones"
                  itemsLabel="Mostrando:"
                />
              }
            />
          </div>
        </div>

        <CotizacionForm
          isOpen={mostrarFormulario}
          closeModal={() => {
            setMostrarFormulario(false);
            setCotizacionParaEditar(null);
            setCloneMode(false);
          }}
          cotizacion={cotizacionParaEditar}
          cloneMode={cloneMode}
        />

        <ModalPreviaCotizacion
          isOpen={openPrevia}
          onClose={() => setOpenPrevia(false)}
          cotizacion={cotizacionActiva}
          onEdit={(c) => {
            setOpenPrevia(false);
            setCotizacionParaEditar(c);
            setCloneMode(false);
            setMostrarFormulario(true);
          }}
          onCopy={(c) => {
            setOpenPrevia(false);
            setCotizacionParaEditar(c);
            setCloneMode(true);
            setMostrarFormulario(true);
          }}
          onSendEmail={(c) => {
            const mailto = buildMailtoForCotizacion(c);
            window.location.href = mailto;
          }}
          onView={(c) => {
            setOpenPrevia(false);
            setCotizacionPdf(c);
            setOpenPdfPreview(true);
          }}
        />

        <PdfPreviewCotizacionModal
          isOpen={openPdfPreview}
          onClose={() => {
            setOpenPdfPreview(false);
            setCotizacionPdf(null);
          }}
          cotizacion={cotizacionPdf}
          tipo="COTIZACIÓN"
        />

        {/*<ModalEnviarCotizacion
        isOpen={openEnviarEmail}
        onClose={() => {
          setOpenEnviarEmail(false);
          setCotizacionParaEnviar(null);
        }}
        cotizacion={cotizacionParaEnviar}
        onSend={async ({ to, subject, message, cotizacion }) => {
          // aquí vas a conectar tu envío real (EmailJS / Cloud Function / nodemailer / etc.)
          console.log("Enviar a:", to);
          console.log("Asunto:", subject);
          console.log("Mensaje:", message);
          console.log("Cotizacion:", cotizacion);
        }}
      />*/}
      </div>
    </>
  );
};

export default TablaCotizaciones;
